import type { User } from '@/types/auth';
import type { OrganizationKey } from '@/types/user-admin';
import { ENABLED_WEEKEND_DISCHARGE_HOSPITALS } from '@/constant';
export const ADMIN_ROLE_KEY = 'ADMIN';
export const SUPERVISOR_ROLE_KEY = 'SUPERVISOR';
export const STAFF_ROLE_KEY = 'STAFF';
export const ADMIN_HOME_PATH = '/admin/hospitals';
export const SUPERVISOR_HOME_PATH = '/supervisor/scorecard';
export const OTHER_THAN_ADMIN_HOME_PATH_1 = '/dashboard';
export const OTHER_THAN_ADMIN_HOME_PATH_2 = '/patients';
export const ORGANIZATION_KEYS: Record<string, OrganizationKey> = {
    SILVERSTAY: 'SILVERSTAY',
    HOSPITAL: 'HOSPITAL'
};

const hasRoleKey = (user: User | null | undefined, roleKey: string): boolean => {
    if (!user?.role) return false;
    const normalized = user.role.key?.toUpperCase();
    if (!normalized) return false;
    return normalized === roleKey.toUpperCase();
};

export const isAdmin = (user: User | null | undefined): boolean => hasRoleKey(user, ADMIN_ROLE_KEY);

export const isAdminRoute = (pathname: string): boolean => pathname.startsWith('/admin') || pathname.startsWith('/activity-logs');

/** Resolve organization for the logged-in user (`GET /users/me`). */
export const resolveUserOrganizationKey = (user: User | null | undefined): OrganizationKey => {
    if (!user) {
        return ORGANIZATION_KEYS.HOSPITAL;
    }
    if (user.organization?.key === ORGANIZATION_KEYS.SILVERSTAY || user.organization?.key === ORGANIZATION_KEYS.HOSPITAL) {
        return user.organization?.key;
    }
    return ORGANIZATION_KEYS.HOSPITAL;
};

export const isSilverStayUser = (user: User | null | undefined): boolean =>
    resolveUserOrganizationKey(user) === ORGANIZATION_KEYS.SILVERSTAY;

export const isSupervisor = (user: User | null | undefined): boolean => hasRoleKey(user, SUPERVISOR_ROLE_KEY);

export const isStaff = (user: User | null | undefined): boolean => hasRoleKey(user, STAFF_ROLE_KEY);

export const isStaffOrSupervisor = (user: User | null | undefined): boolean => isStaff(user) || isSupervisor(user);

/** Sticky Notes column: Admin, or Hospital supervisor (not SilverStay supervisor/staff). */
export const canViewStickyNoteColumn = (user: User | null | undefined): boolean =>
    isAdmin(user) || (isSupervisor(user) && !isSilverStayUser(user));

/** LLOS note edit: Admin, hospital staff (navigators), or hospital supervisor — not SilverStay supervisors. */
export const canEditLlosNote = (user: User | null | undefined): boolean =>
    isAdmin(user) || isStaff(user) || (isSupervisor(user) && !isSilverStayUser(user));

/** In-app notifications and Socket.IO are enabled for every signed-in role. */
export const shouldEnableNotifications = (user: User | null | undefined): boolean => Boolean(user);

export const getUserHomePath = (user: User | null | undefined): string => {
    if (isAdmin(user)) return ADMIN_HOME_PATH;
    if (isSupervisor(user)) return isSilverStayUser(user) ? OTHER_THAN_ADMIN_HOME_PATH_2 : SUPERVISOR_HOME_PATH;
    const defaultHospitalName =
        user?.hospitals
            ?.find(({ isDefault }) => isDefault)
            ?.name?.trim()
            ?.toLowerCase() ?? '';

    const isWeekendDischargeEnabled = ENABLED_WEEKEND_DISCHARGE_HOSPITALS.includes(defaultHospitalName);
    return isWeekendDischargeEnabled ? OTHER_THAN_ADMIN_HOME_PATH_1 : OTHER_THAN_ADMIN_HOME_PATH_2;
};
